/**
 * ChannelsService - HTTP client for the "Gestión de Canales" module.
 * Encapsulates all fetch calls against the distribution API, auth header
 * injection and error handling so presentational components stay free of raw
 * HTTP. Mirrors the AccountsUsersService conventions.
 */
import {
    urlDistribution,
    endpointChannelsAdminGet,
    endpointChannelsAdminSet,
    endpointChannelsAdminDelete,
    endpointChannelsAdminGetCategories,
    endpointChannelsAdminRevealPassword,
} from "./Endpoints";
import AuthService from "./AuthService";

class ChannelsService {
    async request(endpoint, options = {}) {
        const token = AuthService.getToken();
        const headers = { ...(options.headers || {}) };
        headers['Content-Type'] = 'application/json';
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${urlDistribution}${endpoint}`, {
            method: options.method || 'GET',
            headers,
            body: options.body,
        });

        if (!response.ok) {
            throw new Error('Error de conexión con el servidor');
        }

        let res;
        try {
            res = await response.json();
        } catch (e) {
            throw new Error('Respuesta no válida del servidor');
        }

        if (!res || res.status !== 'success') {
            const msg = (res && res.msg) ? res.msg : 'Ocurrió un error';
            const error = new Error(msg);
            error.status = res ? res.status : 'error';
            error.code = res ? res.code : undefined;
            error.data = res ? res.data : undefined;
            throw error;
        }

        return res.data;
    }

    buildParams(obj) {
        const params = new URLSearchParams();
        Object.keys(obj).forEach((k) => {
            const v = obj[k];
            if (v !== '' && v !== undefined && v !== null) {
                params.append(k, v);
            }
        });
        return params.toString();
    }

    async getChannels(filters = {}) {
        const query = this.buildParams(filters);
        return this.request(`${endpointChannelsAdminGet}${query ? `?${query}` : ''}`);
    }

    async setChannel(payload) {
        return this.request(endpointChannelsAdminSet, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    async deleteChannel(id) {
        return this.request(endpointChannelsAdminDelete, {
            method: 'POST',
            body: JSON.stringify({ _id: id }),
        });
    }

    async revealConnectionPassword(channelId, connectionId) {
        return this.request(endpointChannelsAdminRevealPassword, {
            method: 'POST',
            body: JSON.stringify({ _id: channelId, connection_id: connectionId }),
        });
    }

    async getCategories({ type = 'bisac', q = '', codes = '', page = 1, per_page = 50 } = {}) {
        const query = this.buildParams({ type, q, codes, page, per_page });
        return this.request(`${endpointChannelsAdminGetCategories}?${query}`);
    }
}

const ChannelsServiceInstance = new ChannelsService();

export { ChannelsServiceInstance as ChannelsService };
export default ChannelsServiceInstance;